import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addDays, format, parseISO } from "date-fns";
import { rescheduleSchedule } from "@/api/schedule";
import { ScheduleType, type ScheduleDetail } from "@/api/schedule/models";
import { ModalAlert } from "@/components/common/modals";
import { ScheduleForm } from "@/components/features/schedule/ScheduleForm";
import { useDepartmentNavigate } from "@/hooks/common/use-department-navigate";
import { useForm } from "@/hooks/common/use-form";
import { showToast } from "@/providers";
import { RoutePathEnum } from "@/routes/paths";
import { formatDateInSaoPauloTimeZone } from "@/utils/date";
import { verifyIfTalentIsAdult } from "@/utils/features/talent/validations";

type ReschedulePhotoRenewalModalProps = {
  isOpen: boolean;
  onClose: () => void;
  schedule: ScheduleDetail;
};

type FormFields =
  | "scheduleType"
  | "photoMaterialType"
  | "talentCode"
  | "candidateFullName"
  | "candidateBirthday"
  | "candidateEmail"
  | "candidatePhone"
  | "candidateSecondaryPhone"
  | "responsibleId"
  | "responsibleEmail"
  | "responsiblePhone"
  | "responsibleSecondaryPhone"
  | "scheduleDate"
  | "scheduleTime";

export function ReschedulePhotoRenewalModal({
  isOpen,
  onClose,
  schedule,
}: ReschedulePhotoRenewalModalProps): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useDepartmentNavigate();
  const today = parseISO(formatDateInSaoPauloTimeZone());
  const maximumDate = addDays(today, 6);
  const talent = schedule.participant.talent;
  const responsibles = talent?.responsibles ?? [];
  const isMinor = schedule.participant.birthday ? !verifyIfTalentIsAdult(schedule.participant.birthday) : false;
  const initialValues = {
    scheduleType: ScheduleType.PHOTO_RENEWAL,
    photoMaterialType: schedule.photoMaterialType ?? "",
    talentCode: talent?.talentCode ?? "",
    candidateFullName: schedule.participant.fullName,
    candidateBirthday: schedule.participant.birthday ? new Date(schedule.participant.birthday) : null,
    candidateEmail: talent?.email ?? "",
    candidatePhone: talent?.phone ?? "",
    candidateSecondaryPhone: talent?.secondaryPhone ?? "",
    responsibleId: isMinor ? (responsibles[0]?.id ?? "") : "",
    responsibleEmail: responsibles[0]?.email ?? "",
    responsiblePhone: responsibles[0]?.phone ?? "",
    responsibleSecondaryPhone: responsibles[0]?.secondaryPhone ?? "",
    scheduleDate: null,
    scheduleTime: "",
  };

  const { action, control } = useForm<null, FormFields>({
    initialValues,
    fields: {
      scheduleType: { validation: "none" },
      photoMaterialType: { validation: "none" },
      talentCode: { validation: "none" },
      candidateFullName: { validation: "none" },
      candidateBirthday: { validation: "none" },
      candidateEmail: { validation: "none" },
      candidatePhone: { validation: "none" },
      candidateSecondaryPhone: { validation: "none" },
      responsibleId: { validation: "none" },
      responsibleEmail: { validation: "none" },
      responsiblePhone: { validation: "none" },
      responsibleSecondaryPhone: { validation: "none" },
      scheduleDate: { validation: "required" },
      scheduleTime: { validation: "required" },
    },
    handleSubmit: async (data) => {
      await rescheduleMutation.mutateAsync({
        scheduleDate: format(data.scheduleDate as Date, "yyyy-MM-dd"),
        scheduleTime: data.scheduleTime,
        photoRenewal: isMinor && data.responsibleId ? { responsibleId: data.responsibleId } : {},
      });
      return null;
    },
    options: { onSubmitError: () => true },
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({ scheduleDate, scheduleTime, photoRenewal }: Parameters<typeof rescheduleSchedule>[1]) =>
      rescheduleSchedule(schedule.id, { scheduleDate, scheduleTime, photoRenewal }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["schedule", "agenda"], refetchType: "active" }),
        queryClient.invalidateQueries({ queryKey: ["schedule", "stage-counts"], refetchType: "active" }),
      ]);
      showToast({ type: "success", title: "Agendamento atualizado com sucesso!" });
      control.reset(initialValues);
      onClose();
      navigate(`${RoutePathEnum.SCHEDULE}?date=${variables.scheduleDate}`);
    },
    onError: () => {
      showToast({
        type: "error",
        title: "Erro ao atualizar o agendamento.",
        content: "Tente novamente em alguns instantes.",
      });
    },
  });

  const handleClose = () => {
    if (!rescheduleMutation.isPending) {
      control.reset(initialValues);
      onClose();
    }
  };

  return (
    <ModalAlert
      isOpen={isOpen}
      onClose={rescheduleMutation.isPending ? undefined : handleClose}
      className="max-w-[800px]"
      title="Reagendar renovação de fotos"
      description="Informe nova data e horário para o atendimento."
      actions={[
        ["Voltar", handleClose],
        {
          children: "Reagendar",
          type: "submit",
          form: "reschedule-photo-renewal-form",
          isLoading: rescheduleMutation.isPending,
        },
      ]}
    >
      <form id="reschedule-photo-renewal-form" className="form-flow" onSubmit={action}>
        <ScheduleForm
          control={control}
          mode="reschedulePhotoRenewal"
          isMinor={isMinor}
          minimumDate={today}
          maximumDate={maximumDate}
          photoRenewal={{
            scheduleType: ScheduleType.PHOTO_RENEWAL,
            photoMaterialType: schedule.photoMaterialType ?? null,
            talentCode: talent?.talentCode ?? "",
            responsibles,
          }}
        />
      </form>
    </ModalAlert>
  );
}
